import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Copy, MoveRight, AlertTriangle, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { SearchableCombobox } from '../SearchableCombobox';
import { isActiveProject } from '../../lib/projectStatus';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const NEW_BOQ = '__new__';

// Fix 6: Copy / Move selected BOQs into another active project.
export const TransferBoqDialog = ({ open, onOpenChange, mode, ids, projects, sourceProjectIds = [], onDone }) => {
  const [projectId, setProjectId] = useState('');
  const [targetWs, setTargetWs] = useState(NEW_BOQ);
  const [projectBoqs, setProjectBoqs] = useState([]);
  const [links, setLinks] = useState(null);
  const [busy, setBusy] = useState(false);
  const isMove = mode === 'move';
  const idsKey = ids.join(',');

  useEffect(() => {
    if (!open) return;
    setProjectId(''); setTargetWs(NEW_BOQ); setLinks(null);
    if (isMove && ids.length) {
      axios.post(`${API}/boq-worksheets/transfer/check`, { ids, target_project_id: '-', mode })
        .then(r => setLinks(r.data)).catch(() => setLinks(null));
    }
  }, [open, isMove, idsKey]);

  useEffect(() => {
    setTargetWs(NEW_BOQ); setProjectBoqs([]);
    if (!projectId) return;
    axios.get(`${API}/boq-worksheets?project_id=${projectId}&active_filter=active`)
      .then(r => setProjectBoqs((r.data || []).filter(w => w.project_id === projectId && !ids.includes(w.id) && !w.is_locked)))
      .catch(() => setProjectBoqs([]));
  }, [projectId, idsKey]);

  const options = useMemo(() => [...(projects || [])]
    .filter(p => isActiveProject(p) && !(isMove && sourceProjectIds.length === 1 && sourceProjectIds[0] === p.id))
    .sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''))
    .map(p => ({ value: p.id, label: p.name })), [projects, isMove, sourceProjectIds]);

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/boq-worksheets/transfer`, {
        ids, target_project_id: projectId, mode,
        target_worksheet_id: targetWs === NEW_BOQ ? null : targetWs,
      });
      toast.success(data.message);
      (data.warnings || []).forEach(w => toast.warning(w));
      onOpenChange(false);
      onDone?.(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || `Failed to ${mode} BOQ`);
    } finally { setBusy(false); }
  };

  const Icon = isMove ? MoveRight : Copy;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md bg-white/80 backdrop-blur-xl border-white/60" data-testid={`transfer-boq-dialog-${mode}`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Icon size={18} />{isMove ? 'Move' : 'Copy'} {ids.length} BOQ{ids.length === 1 ? '' : 's'} to Project</DialogTitle>
          <DialogDescription>
            {isMove ? 'The BOQ is reassigned to the destination and removed from its current project.' : 'All line items, headers, quantities and rates are duplicated. The source BOQ is unchanged.'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-wider text-stone-500">Destination project</Label>
            <SearchableCombobox options={options} value={projectId} onValueChange={setProjectId} placeholder="Select an active project…" data-testid="transfer-project-picker" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-stone-500">Destination BOQ</Label>
            <Select value={targetWs} onValueChange={setTargetWs} disabled={!projectId}>
              <SelectTrigger data-testid="transfer-boq-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={NEW_BOQ}>Create as new BOQ</SelectItem>
                {projectBoqs.map(w => (
                  <SelectItem key={w.id} value={w.id}>Add into {w.display_name || w.name || w.worksheet_number}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {isMove && links?.has_links && (
            <div className="flex gap-2 rounded-lg border border-amber-200 bg-amber-50/80 p-3 text-xs text-amber-800" data-testid="transfer-links-warning">
              <AlertTriangle size={16} className="shrink-0 mt-0.5" />
              <div>Linked invoices / payments stay with the original project:
                <ul className="mt-1 list-disc pl-4">{links.boqs.filter(b => b.invoices || b.vendor_payments || b.applied_payments).map(b => (
                  <li key={b.id}>{b.worksheet_number}: {b.invoices} invoice(s), {b.vendor_payments} vendor payment(s), {b.applied_payments} applied client payment(s)</li>
                ))}</ul>
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="transfer-cancel-btn">Cancel</Button>
          <Button onClick={submit} disabled={!projectId || busy} className="rounded-full" data-testid="transfer-confirm-btn">
            {busy ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Icon size={14} className="mr-1.5" />}{isMove ? 'Move' : 'Copy'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
